import { AlertTriangle, CheckCircle2, PackageX, RefreshCw, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

const HIGHLIGHTS = [
  {
    icon: AlertTriangle,
    title: "Report Immediately",
    body:
      "Any damage must be reported right after delivery. Delayed claims may not be accepted, so please inspect your parcel at the time of hand-over.",
    accent: "bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]",
  },
  {
    icon: PackageX,
    title: "Strict Return Policy",
    body:
      "Returns or refunds are NOT applicable for reasons such as change of mind, incorrect selection, or ordering the wrong edition/class.",
    accent: "bg-[#ffedd5] text-[#9a3412] border-[#fed7aa]",
  },
  {
    icon: PackageX,
    title: "No Cancellation After Confirmation",
    body:
      "We do not offer cancellations or returns once an order is confirmed and payment is completed. Please review your order carefully before checkout.",
    accent: "bg-[#fee2e2] text-[#991b1b] border-[#fecaca]",
  },
  {
    icon: ShieldCheck,
    title: "Quality Guaranteed",
    body:
      "We stand by the quality of our books. If your item arrives damaged or defective, we've got you covered — every time.",
    accent: "bg-[#dcfce7] text-[#166534] border-[#bbf7d0]",
  },
  {
    icon: RefreshCw,
    title: "Prompt Replacement",
    body:
      "We'll promptly send a replacement of the same item, subject to availability from the publisher or our warehouse stock.",
    accent: "bg-[#e0e7ff] text-[#3730a3] border-[#c7d2fe]",
  },
  {
    icon: CheckCircle2,
    title: "Hassle-Free Refund",
    body:
      "If a replacement isn't available, you'll receive a hassle-free full refund credited back to your original payment method.",
    accent: "bg-[#fef3c7] text-[#92400e] border-[#fde68a]",
  },
  {
    icon: Sparkles,
    title: "Fair & Fast Resolution",
    body:
      "Your satisfaction matters — every issue is resolved quickly and fairly. Simple process, no stress: just report it, we handle the rest.",
    accent: "bg-[#fae8ff] text-[#86198f] border-[#f5d0fe]",
  },
];

const STEPS = [
  { n: "01", t: "Inspect at Delivery", d: "Open the parcel in front of the delivery agent whenever possible and check for visible damage." },
  { n: "02", t: "Report Within 24 Hours", d: "Call/WhatsApp us on +91 99532 72630 with your Order ID and clear photos of the damaged item." },
  { n: "03", t: "We Verify & Act", d: "Our team reviews the claim within 24 working hours and confirms replacement or refund eligibility." },
  { n: "04", t: "Replacement or Refund", d: "Replacement is dispatched if in stock; otherwise a full refund is issued to your original payment method." },
];

export default function ReturnPolicy() {
  return (
    <div className="bg-[#fafaf9] min-h-screen" data-testid="return-policy-page">
      {/* HERO */}
      <section className="bg-[#09090b] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)", backgroundSize: "24px 24px" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 relative">
          <div className="inline-flex items-center gap-2 border border-[#ea580c]/40 bg-[#ea580c]/10 text-[#fb923c] px-3 py-1.5 text-xs font-bold tracking-[0.2em] uppercase mb-6">
            <ShieldCheck className="w-3.5 h-3.5" /> Customer Protection
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[0.95] max-w-4xl" data-testid="return-policy-title">
            Return &amp; <span className="text-[#fb923c]">Non-Refundable</span> Policy
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#a1a1aa] max-w-2xl leading-relaxed">
            Transparent rules, quick action. Read this before you order — so if anything goes wrong, we can make it right without any stress.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a
              href="tel:9953272630"
              className="inline-flex items-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              data-testid="return-policy-call-btn"
            >
              Report an Issue — Call +91 99532 72630
            </a>
            <a
              href="https://wa.me/919953272630"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 border border-white/20 hover:border-white/50 text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              data-testid="return-policy-whatsapp-btn"
            >
              WhatsApp Support
            </a>
          </div>
        </div>
      </section>

      {/* HIGHLIGHTS GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="mb-10">
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">The Policy in 6 Points</div>
          <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b] max-w-3xl">
            Everything you need to know — clearly written, no fine print.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="policy-highlights-grid">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div
                key={h.title}
                className="bg-white border border-[#e7e5e4] p-6 hover:border-[#09090b] transition-colors group"
                data-testid={`policy-card-${i}`}
              >
                <div className={`inline-flex w-11 h-11 items-center justify-center border ${h.accent}`}>
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <div className="mt-5 font-display text-lg font-black text-[#09090b] leading-tight">{h.title}</div>
                <p className="mt-2 text-sm text-[#57534e] leading-relaxed">{h.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* STEPS */}
      <section className="bg-white border-y border-[#e7e5e4]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c] mb-3">How the process works</div>
              <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b]">
                From issue to resolution in 4 simple steps.
              </h2>
            </div>
            <p className="text-sm text-[#57534e] max-w-sm">
              Most damage claims are resolved within 48 hours of a valid report. Keep your order ID handy.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-[#e7e5e4]">
            {STEPS.map((s, idx) => (
              <div
                key={s.n}
                className={`p-6 md:p-8 bg-[#fafaf9] ${idx !== STEPS.length - 1 ? "lg:border-r border-[#e7e5e4]" : ""}`}
                data-testid={`policy-step-${idx}`}
              >
                <div className="font-display text-5xl font-black text-[#ea580c]/20 leading-none">{s.n}</div>
                <div className="mt-4 font-display text-lg font-black text-[#09090b]">{s.t}</div>
                <p className="mt-2 text-sm text-[#57534e] leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IS NOT COVERED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#b91c1c] mb-3">Not Eligible</div>
            <h2 className="font-display text-3xl md:text-4xl font-black text-[#09090b] mb-5">
              What is <span className="underline decoration-[#ea580c] decoration-4 underline-offset-4">not</span> covered
            </h2>
            <ul className="space-y-3 text-[#3f3f46]" data-testid="policy-not-covered">
              {[
                "Change of mind after delivery",
                "Wrong book / wrong edition / wrong class selected while placing the order",
                "Minor cover creases from normal shipping handling",
                "Damage reported after 24 hours of delivery",
                "Books marked as 'Old & Rare' or 'Clearance' — sold as-is",
                "Digital / downloadable products once accessed",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm">
                  <span className="mt-1 w-4 h-4 shrink-0 border-2 border-[#b91c1c] flex items-center justify-center">
                    <span className="w-2 h-0.5 bg-[#b91c1c]" />
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#09090b] text-white p-8 md:p-10">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#fb923c] mb-3">We stand behind every book</div>
            <h3 className="font-display text-2xl md:text-3xl font-black leading-tight">
              Damaged parcel? Missing pages? Wrong item shipped from our end?
            </h3>
            <p className="mt-4 text-sm text-[#a1a1aa] leading-relaxed">
              Reach out immediately and we'll take care of it. Replacement first, refund if not available — no long forms, no arguments.
            </p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href="tel:9953272630"
                className="inline-flex items-center justify-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              >
                Call Now
              </a>
              <a
                href="https://wa.me/919953272630?text=Hi%20GAINA%20THE%20PRELUDE%2C%20I%20want%20to%20report%20a%20damaged%20order."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-5 py-3 text-sm font-bold tracking-wide uppercase transition-colors"
              >
                WhatsApp Us
              </a>
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 text-xs text-[#a1a1aa] leading-relaxed">
              GAINA THE PRELUDE • New Delhi, India<br />
              Email: gainatheprelude@gmail.com
            </div>
          </div>
        </div>

        <div className="mt-14 text-center">
          <Link to="/books" className="inline-flex items-center gap-2 border-b-2 border-[#09090b] text-[#09090b] font-bold text-sm tracking-wide uppercase pb-1 hover:text-[#ea580c] hover:border-[#ea580c] transition-colors" data-testid="policy-continue-shopping">
            Continue Shopping →
          </Link>
        </div>
      </section>
    </div>
  );
}
